import { motion } from "framer-motion";
import { GraduationCap, Users, Camera, Star, MessageCircle, MapPin, Heart, BookOpen, Globe, School, ArrowRight } from "lucide-react";

const PROGRAMS = [
  {
    title: "Google My Business per ragazzi",
    desc: "I ragazzi imparano a creare e gestire una scheda Google per l'attivita' dei genitori o del quartiere.",
    target: "Medie e Superiori",
    icon: Globe,
    color: "#4285F4",
  },
  {
    title: "Fotografia 360° e Street View",
    desc: "Laboratorio pratico con GoPro MAX: i ragazzi fotografano la scuola e la pubblicano su Google Maps.",
    target: "Elementari e Medie",
    icon: Camera,
    color: "#34A853",
  },
  {
    title: "Reputazione digitale",
    desc: "Come funzionano le recensioni, perche' contano, come rispondere. Educazione al digitale consapevole.",
    target: "Medie e Superiori",
    icon: Star,
    color: "#FBBC04",
  },
  {
    title: "Il mio quartiere su Google Maps",
    desc: "Progetto di classe: mappare le attivita' del quartiere, intervistare i commercianti, creare contenuti.",
    target: "Elementari",
    icon: MapPin,
    color: "#EA4335",
  },
];

const COLLABS = [
  { title: "Scuole elementari", desc: "Laboratori creativi di mappatura digitale del territorio", icon: BookOpen, color: "#EA4335" },
  { title: "Scuole medie", desc: "Percorsi di educazione digitale e fotografia 360°", icon: GraduationCap, color: "#FBBC04" },
  { title: "Scuole superiori", desc: "Alternanza scuola-lavoro e progetti di marketing territoriale", icon: School, color: "#4285F4" },
  { title: "Associazioni locali", desc: "Partnership per eventi di formazione digitale sul territorio", icon: Users, color: "#34A853" },
];

const WA_MSG = "Ciao Riccardo! Sono interessato al programma S.it Cares #its4kids per la nostra scuola/associazione.";

export default function Academy() {
  return (
    <div className="min-h-screen bg-[#0a0e17] text-white">
      {/* Hero */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#EA4335]/10 to-transparent" />
        <div className="max-w-4xl mx-auto relative text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="text-3xl font-black">
                <span style={{ color: "#4285F4" }}>S</span><span className="text-white/60">.it</span>
              </div>
            </div>
            <h1 className="text-5xl sm:text-6xl font-black mb-4">
              <span className="text-[#EA4335]">Cares</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-6">
              Il programma di responsabilita' sociale di schedabusiness.it
            </p>
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#EA4335]/10 border border-[#EA4335]/30">
              <Heart className="w-4 h-4 text-[#EA4335]" />
              <span className="text-[#EA4335] font-bold text-lg">#its4kids</span>
            </div>
          </motion.div>

          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="text-gray-400 text-base mt-8 max-w-xl mx-auto">
            Collaboriamo con associazioni e scuole per formare sul digitale le nuove generazioni. Elementari, medie e superiori.
          </motion.p>

          {/* Level badges */}
          <div className="flex justify-center gap-3 mt-6">
            {["Elementari", "Medie", "Superiori"].map((level, i) => (
              <span key={i} className="px-4 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm text-gray-300 font-medium">{level}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="text-sm text-gray-500 mb-2">La nostra missione</p>
            <h2 className="text-2xl sm:text-3xl font-black mb-6">
              Come NBA Cares sta alla NBA,{" "}
              <span className="text-2xl sm:text-3xl font-black">
                <span style={{ color: "#4285F4" }}>S</span><span className="text-white/60">.it</span>
              </span>{" "}
              sta a schedabusiness.it
            </h2>
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              Il digitale non e' solo business. E' uno strumento che puo' cambiare la vita delle comunita' locali.
              Per questo portiamo la formazione digitale nelle scuole — <strong className="text-white">gratuitamente</strong>.
            </p>
            <blockquote className="border-l-4 border-[#EA4335] pl-6 py-2">
              <p className="text-white italic text-lg">"Il futuro digitale dei nostri ragazzi inizia oggi, nella loro scuola."</p>
              <cite className="text-gray-500 text-sm mt-2 block">— Riccardo Rossi, fondatore schedabusiness.it</cite>
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* Programs */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-gray-500 mb-2">Programmi formativi</p>
          <h2 className="text-2xl sm:text-3xl font-black mb-8">
            Cosa insegniamo <span className="text-[#EA4335]">#its4kids</span>
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {PROGRAMS.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <p.icon className="w-6 h-6" style={{ color: p.color }} />
                  <h3 className="text-white font-bold text-base">{p.title}</h3>
                </div>
                <p className="text-gray-400 text-sm mb-3">{p.desc}</p>
                <span className="px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: `${p.color}15`, color: p.color, border: `1px solid ${p.color}30` }}>
                  {p.target}
                </span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Collaborations */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <p className="text-sm text-gray-500 mb-2">Collaborazioni</p>
          <h2 className="text-2xl sm:text-3xl font-black mb-8">Con chi lavoriamo</h2>
          <div className="grid sm:grid-cols-2 gap-4 mb-10">
            {COLLABS.map((c, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <c.icon className="w-5 h-5" style={{ color: c.color }} />
                  <h3 className="text-white font-bold">{c.title}</h3>
                </div>
                <p className="text-gray-400 text-sm">{c.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center">
            <p className="text-gray-400 text-base mb-6">Sei un dirigente scolastico o un'associazione? Parliamone.</p>
            <a href={`https://wa.me/+393273839782?text=${encodeURIComponent(WA_MSG)}`}
              className="inline-flex items-center gap-2 bg-[#34A853] hover:bg-[#2d9249] text-white font-bold px-8 py-4 rounded-full text-lg transition-all hover:scale-105">
              <MessageCircle className="w-5 h-5" /> Contattaci su WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-2xl font-black">
              <span style={{ color: "#4285F4" }}>S</span><span className="text-white/60">.it</span>
            </span>
          </div>
          <h3 className="text-xl font-black text-[#EA4335] mb-1">Cares</h3>
          <p className="text-[#EA4335] text-sm font-bold">#its4kids</p>
          <p className="text-gray-500 text-xs mt-4">Elementari · Medie · Superiori</p>
          <p className="text-gray-600 text-xs mt-1">Un progetto di schedabusiness.it per le nuove generazioni</p>
        </div>
      </footer>
    </div>
  );
}
